import { DateTime } from 'luxon';
import { axiosInstance } from '@/shared/lib/axios';
import type { ShopSetupStatus } from '@/shared/types/domain';
import type {
  PaginatedResponse,
  PaginationParams,
} from '@/shared/types/domain/pagination.types';
import type {
  PaymentRoutingMode,
  ShopPaymentPolicy,
} from '@/shared/types/domain/payment-capability.types';
import type { Service } from '@/shared/types/domain/service.types';
import type {
  ShopResponse,
  ShopCreateInput,
  ShopUpdateInput,
  LocationResponse,
  LocationUpdateInput,
  SocialMediaResponse,
  SocialMediaUpdateInput,
  ShopCardViewModel,
} from '@/shared/types/domain/shop-extended.types';
import type { BookingMode, ProviderType, ServiceFlowMode, ShopVertical } from '@/shared/types/enums';
import { ShopStatus } from '@/shared/types/enums';
import { logger } from '@/shared/utils/logger';

const BASE_URL = '/api/v1/shops';

// Setup API type definitions
export interface ProfileData {
  name: string;
  phoneNumber: string;
  pictureUrl?: string;
  providerType: ProviderType;
  bio?: string;
  timezone: string;
  address?: string;
}

export interface LicenseData {
  licenseNumber: string;
  licenseType: string;
  issuingState: string;
  issueDate: DateTime;
  expirationDate: DateTime;
  documentUrl: string;
  document?: File | null;
}

export interface PaymentData {
  accountName?: string;
  accountType?: string;
  routingNumber?: string;
  accountNumber?: string;
}

export interface ShopImagesData {
  assetId: string;
  providerId: string;
}

export type ShopImageType = 'PROFILE' | 'GALLERY' | 'INTERIOR' | 'EXTERIOR' | 'LOGO';

export interface ShopImage {
  id: string;
  imageUrl: string;
  type: ShopImageType;
  caption: string | null;
  displayOrder: number;
  size: number | null;
  dimensions: { width: number; height: number } | null;
  variants: { original: string; large: string; medium: string; thumbnail: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface ShopImagesResponse {
  shopId: string;
  count: number;
  images: ShopImage[];
}

export interface ShopImageReview {
  assetId: string;
  status:
    | 'APPROVED'
    | 'ADMIN_APPROVED'
    | 'REJECTED'
    | 'ADMIN_REJECTED'
    | 'NEEDS_REVIEW';
  reasonCode: string | null;
  checkedAt: string | null;
  createdAt: string;
}

export interface ShopImageReviewStatus {
  latestReview: ShopImageReview | null;
  pendingReview: ShopImageReview | null;
}

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface ShopData {
  name: string;
  type?: ProviderType;
  address: string;
  phoneNumber: string;
  emailAddress: string;
  coordinates?: Coordinates;
  shopVertical?: ShopVertical;
  serviceFlowMode?: ServiceFlowMode;
  ownerIsBookable?: boolean;
}

// Type for raw business data from API (backend EnrichedShop type)
interface RawShopResponse {
  id: string;
  ownerId: string;
  name: string;
  type: ProviderType;
  shopVertical?: ShopVertical;
  serviceFlowMode?: ServiceFlowMode | null;
  address: string;
  phoneNumber: string | null;
  emailAddress: string | null;
  imageUrl: string;
  logoUrl?: string | null;
  status: ShopStatus;
  bookingMode?: BookingMode;
  paymentRoutingMode?: PaymentRoutingMode;
  paymentPolicy?: ShopPaymentPolicy | null;
  rating: number;
  total_ratings: number;
  neighborhood: string;
  price_range: string;
  location: {
    id: string;
    shopId: string;
    latitude: number | null;
    longitude: number | null;
  } | null;
  socialMedia: {
    id: string;
    shopId: string;
    facebookUrl: string | null;
    instagramUrl: string | null;
    twitterUrl: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
  services?: { name: string }[];
}

/**
 * Transform raw API response to clean Shop type with DateTime objects
 */
const transformShopResponse = (raw: RawShopResponse): ShopResponse => {
  return {
    id: raw.id,
    ownerId: raw.ownerId,
    name: raw.name,
    type: raw.type,
    shopVertical: raw.shopVertical,
    serviceFlowMode: raw.serviceFlowMode,
    address: raw.address,
    phoneNumber: raw.phoneNumber,
    emailAddress: raw.emailAddress,
    imageUrl: raw.imageUrl,
    logoUrl: raw.logoUrl ?? null,
    status: raw.status,
    bookingMode: raw.bookingMode,
    paymentRoutingMode: raw.paymentRoutingMode,
    paymentPolicy: raw.paymentPolicy ?? null,
    location: raw.location,
    socialMedia: raw.socialMedia,
    createdAt: DateTime.fromISO(raw.createdAt),
    updatedAt: DateTime.fromISO(raw.updatedAt),
    rating: raw.rating,
    total_ratings: raw.total_ratings,
    neighborhood: raw.neighborhood,
    price_range: raw.price_range,
    services: raw.services?.map(s => s.name),
  };
};

/**
 * Convert Shop to ShopCardViewModel for UI components
 */
export const shopToCardViewModel = (shop: ShopResponse): ShopCardViewModel => {
  return {
    id: shop.id,
    name: shop.name,
    imageUrl: shop.imageUrl,
    logoUrl: shop.logoUrl ?? null,
    address: shop.address,
    rating: shop.rating,
    total_ratings: shop.total_ratings,
    neighborhood: shop.neighborhood,
    price_range: shop.price_range,
    verified: shop.status === ShopStatus.ACTIVE,
    location: shop.location,
  };
};

export const shop = {
  fetchShops: async (
    params?: PaginationParams,
  ): Promise<PaginatedResponse<ShopResponse>> => {
    try {
      const response = await axiosInstance.get(BASE_URL, { params });
      logger.debug('Raw API response:', response.data);

      return {
        data: response.data.data.map(transformShopResponse),
        meta: response.data.meta,
      };
    } catch (error) {
      logger.error('Failed to fetch shops:', error);
      // Return empty paginated response instead of throwing to prevent UI crashes
      return {
        data: [],
        meta: {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }
  },

  fetchShopsByProviderId: async (
    providerId: string,
  ): Promise<ShopResponse[]> => {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/providers/${providerId}`,
      );
      return response.data.map(transformShopResponse);
    } catch (error) {
      logger.error('Failed to fetch shops by provider:', error);
      // Return empty array instead of throwing to prevent UI crashes
      return [];
    }
  },

  createShop: async (shopData: ShopCreateInput): Promise<ShopResponse> => {
    const response = await axiosInstance.post(BASE_URL, shopData);
    return transformShopResponse(response.data);
  },

  getShop: async (id: string): Promise<ShopResponse | null> => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/${id}`);
      return transformShopResponse(response.data);
    } catch (error) {
      logger.error(`Failed to fetch shop ${id}:`, error);
      // Return null for not found or other errors
      return null;
    }
  },

  updateShop: async (
    id: string,
    updates: ShopUpdateInput,
  ): Promise<ShopResponse> => {
    const response = await axiosInstance.put(`${BASE_URL}/${id}`, updates);
    return transformShopResponse(response.data);
  },

  deleteShop: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/${id}`);
  },

  // Location endpoints
  getShopLocation: async (shopId: string): Promise<LocationResponse | null> => {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/${shopId}/location`,
      );
      return response.data;
    } catch (error) {
      logger.error(`Failed to fetch location for shop ${shopId}:`, error);
      return null;
    }
  },

  updateShopLocation: async (
    shopId: string,
    updates: LocationUpdateInput,
  ): Promise<LocationResponse> => {
    const response = await axiosInstance.put(
      `${BASE_URL}/${shopId}/location`,
      updates,
    );
    return response.data;
  },

  // Social Media endpoints
  getShopSocialMedia: async (
    shopId: string,
  ): Promise<SocialMediaResponse | null> => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/shop-profile/${shopId}/social-media`,
      );
      return response.data;
    } catch (error) {
      logger.error(`Failed to fetch social media for shop ${shopId}:`, error);
      return null;
    }
  },

  updateShopSocialMedia: async (
    shopId: string,
    updates: SocialMediaUpdateInput,
  ): Promise<SocialMediaResponse> => {
    const response = await axiosInstance.post(
      `/api/v1/shop-profile/${shopId}/social-media`,
      updates,
    );
    return response.data;
  },

  // Staff Invitation endpoints
  createStaffInvitation: async (
    shopId: string,
    data: {
      email: string;
      role: string;
      contractType?: string;
      commissionRate?: number;
      boothRentalTiers?: {
        threshold: number;
        feeAmount: number;
        feeType: 'FLAT_RATE' | 'PERCENTAGE';
      }[];
    },
  ): Promise<{ id: string; code: string; email: string; role: string }> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/${shopId}/invitations`,
      data,
    );
    return response.data;
  },

  getInvitation: async (
    code: string,
  ): Promise<{ id: string; code: string; email: string; role: string }> => {
    const response = await axiosInstance.get(`${BASE_URL}/invitations/${code}`);
    return response.data;
  },

  validateInvitation: async (
    code: string,
  ): Promise<{
    isValid: boolean;
    reason?: 'expired' | 'used' | 'not_found' | 'invalid';
  }> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/invitations/${code}/validate`,
    );
    return response.data;
  },

  getServiceCategories: async (isActive?: boolean) => {
    const response = await axiosInstance.get(`/api/v1/services/categories`, {
      params: {
        isActive,
      },
    });
    // Ensure we always return an array
    return Array.isArray(response.data) ? response.data : [];
  },

  getServiceTemplates: async (
    serviceKind?: string,
    context?: { shopId?: string | null; providerType?: string | null },
  ) => {
    const params: Record<string, string> = {};
    if (serviceKind) params.serviceKind = serviceKind;
    if (context?.shopId) params.shopId = context.shopId;
    if (context?.providerType) params.providerType = context.providerType;
    const response = await axiosInstance.get('/api/v1/services/templates', {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data;
  },

  getServiceTemplateImages: async (templateId: string) => {
    const response = await axiosInstance.get(
      `/api/v1/services/templates/${templateId}/images`,
    );
    return response.data;
  },

  getCatalogImages: async (context?: {
    shopId?: string | null;
    providerType?: string | null;
  }): Promise<{
    images: {
      id: string;
      imageUrl: string;
      originalName: string | null;
      displayOrder: number;
      templateId: string;
      templateName: string;
      categoryId: string;
      categoryName: string;
    }[];
  }> => {
    const params: Record<string, string> = {};
    if (context?.shopId) params.shopId = context.shopId;
    if (context?.providerType) params.providerType = context.providerType;
    const response = await axiosInstance.get('/api/v1/services/catalog-images', {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data ?? { images: [] };
  },

  getShopServices: async (shopId: string): Promise<Service[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/${shopId}/services`);
    return response.data;
  },

  // Shop Hours endpoints
  getShopHours: async (
    shopId: string,
  ): Promise<{
    openingHours: Record<string, {
        isOpen: boolean;
        slots: { open: string; close: string }[];
      }>;
    isDefault?: boolean;
  } | null> => {
    try {
      const response = await axiosInstance.get(`/api/v1/shop-hours/${shopId}`);
      return response.data;
    } catch (error) {
      // If it's a 404, the shop might not exist or hours aren't set yet
      if (error.response?.status === 404) {
        logger.debug(`No shop hours found for ${shopId}, using defaults`);
      } else {
        logger.error(`Failed to fetch shop hours for ${shopId}:`, error);
      }
      return null;
    }
  },

  updateShopHours: async (
    shopId: string,
    openingHours: Record<string, {
        isOpen: boolean;
        slots: { open: string; close: string }[];
      }>,
  ): Promise<{
    message: string;
    data: {
      openingHours: Record<string, {
          isOpen: boolean;
          slots: { open: string; close: string }[];
        }>;
    };
  }> => {
    const response = await axiosInstance.post(`/api/v1/shop-hours/${shopId}`, {
      openingHours,
    });
    return response.data;
  },

  resetShopHours: async (
    shopId: string,
  ): Promise<{
    message: string;
    data: {
      openingHours: Record<string, {
          isOpen: boolean;
          slots: { open: string; close: string }[];
        }>;
    };
  }> => {
    const response = await axiosInstance.delete(`/api/v1/shop-hours/${shopId}`);
    return response.data;
  },

  // Setup API endpoints
  updateSetupStatus: async (
    shopId: string,
    providerId: string,
    status: ShopSetupStatus,
  ) => {
    if (!providerId) {
      logger.error('Provider ID not found');
    }

    logger.debug('shopId', shopId);
    logger.debug('providerId', providerId);
    logger.debug('status', status);

    const response = await axiosInstance.put(
      `${BASE_URL}/${shopId}/setup/status`,
      { status, providerId },
    );
    logger.debug('Update setup status response:', response.data);
    return response.data;
  },

  getSetupProgress: async (shopId: string) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/${shopId}/setup/progress`,
    );
    return response.data;
  },

  setupShop: async (data: { shop: ShopData; providerId?: string }) => {
    logger.debug('Setting up shop with data:', data);

    // Transform frontend coordinates to backend location format
    const { coordinates, ...shopData } = data.shop;
    const payload = {
      ...data,
      shop: {
        ...shopData,
        // Convert { lat, lng } to { latitude, longitude } for backend
        location: coordinates
          ? { latitude: coordinates.lat, longitude: coordinates.lng }
          : undefined,
      },
    };

    const response = await axiosInstance.post(`${BASE_URL}/setup`, payload);
    return response.data;
  },

  getShopImages: async (shopId: string, type?: ShopImageType): Promise<ShopImagesResponse | null> => {
    try {
      const params = type ? { type } : undefined;
      const response = await axiosInstance.get<ShopImagesResponse>(
        `${BASE_URL}/${shopId}/images`,
        { params },
      );
      return response.data;
    } catch (error) {
      logger.error('Error fetching shop images:', error);
      return null;
    }
  },

  getShopImageReviewStatus: async (
    shopId: string,
    type: ShopImageType,
  ): Promise<ShopImageReviewStatus> => {
    const response = await axiosInstance.get<ShopImageReviewStatus>(
      `${BASE_URL}/${shopId}/images/review-status`,
      { params: { type } },
    );
    return response.data;
  },

  updateShopImages: async (shopId: string, data: ShopImagesData) => {
    const response = await axiosInstance.post(
      `${BASE_URL}/${shopId}/shop-images`,
      data,
    );
    return response.data;
  },

  updateShopProfile: async (shopId: string, data: ShopData) => {
    const response = await axiosInstance.put(`${BASE_URL}/${shopId}`, {
      name: data.name,
      type: data.type,
      shopVertical: data.shopVertical,
      serviceFlowMode: data.serviceFlowMode,
      address: data.address,
      phoneNumber: data.phoneNumber,
      emailAddress: data.emailAddress,
    });

    return response.data;
  },

  updateProviderProfile: async (providerId: string, data: ProfileData) => {
    const response = await axiosInstance.put(
      `/api/v1/providers/${providerId}/profile`,
      {
        name: data.name,
        phoneNumber: data.phoneNumber,
        providerType: data.providerType,
        bio: data.bio,
        timezone: data.timezone,
        address: data.address,
      },
    );

    return response.data;
  },

  updateLicense: async (providerId: string, data: LicenseData) => {
    // Handle file upload if there's a document
    if (data.document) {
      const formData = new FormData();

      // Add all license data to form
      Object.entries(data).forEach(([key, value]) => {
        if (key !== 'document') {
          // Convert DateTime to ISO strings for API
          if (value instanceof DateTime) {
            const isoString = value.toISO();
            if (isoString) {
              formData.append(key, isoString);
            }
          } else if (value !== null && value !== undefined) {
            formData.append(key, String(value));
          }
        }
      });

      // Add the document file
      formData.append('document', data.document);

      const response = await axiosInstance.put(
        `/api/v1/providers/${providerId}/licenses`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      return response.data;
    } else {
      // No document to upload, just send the license data
      const licenseData = { ...data };
      delete licenseData.document;

      const response = await axiosInstance.put(
        `/api/v1/providers/${providerId}/licenses`,
        licenseData,
      );

      return response.data;
    }
  },

  updatePayment: async (shopId: string, data: PaymentData) => {
    const response = await axiosInstance.put(
      `${BASE_URL}/${shopId}/payment`,
      data,
    );

    return response.data;
  },
};
